import { Button as PlatformButton } from '@datassential/platform-ui-lib';
import { createPortal } from "react-dom";
import Wrapper from "@components/Wrapper";
import './ConceptLockerCustomBenchmarkSetPopup.scss';
import { CustomBenchmarkSetData } from "@api/useCustomBenchmarkSet";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import HeaderBar from "@components/HeaderBar";
import TextInput from "@components/TextInput";
import { MdKeyboardArrowRight } from "react-icons/md";
import Button from "@components/Button";
import { BsStars } from "react-icons/bs";
import { useCustomBenchmarkSearch } from "@api/useCustomBenchmarkSearch";
import {
    SortingColumns,
    useCustomBenchmarkSetTableConfig,
} from "@pages/ConceptLocker/ConceptLockerCustomBenchmarkSetPopup/useCustomBenchmarkSetTableConfig";
import { TableContext } from "@components/Table/TableContext";
import VirtualizedTable from "@components/Table/VirtualizedTable";
import cn from "classnames";
import { iSelectOption, SelectedOptionsProvider } from "@hooks/useSelectedOptions";
import Loader from "@components/Loader";
import { Typewriter } from "react-simple-typewriter";
import { shuffle } from "@core/utils/array";
import { useFilterSort } from "@hooks/useFilterSort";
import TableSortingContext from "@vms/ItemsTable/TableSortingContext";
import { iSortingOption, iTableSorting, Sort } from "@models";
import { useFiltersContext } from "@core/FiltersContext";
import { useBenchmarkNorm } from "@vms/SelectNorm/useBenchmarkNormType";
import { equalInLC } from "@core/utils/string";

interface IProps {
    onClose: () => void;
    data: CustomBenchmarkSetData | null;
    updateData: (data: CustomBenchmarkSetData) => Promise<void>;
    deleteData: () => void;
    isLoading: boolean;
}

export const INPUT_PLACEHOLDERS = [
    'burger with mushrooms',
    'healthy appetizer',
    'vegetarian pizza',
    'wrap with mediterranean flavors',
    'indulgent dessert',
    'bourbon based cocktail',
    'refreshing summer drink',
    'zesty fried starter',
    'spicy noodle dish',
    'southwest style salad',
    'breakfast sandwich with guacamole',
    'non-alcoholic cocktail',
    'fruit based dessert',
];

export function ConceptLockerCustomBenchmarkSetPopup({
    onClose,
    data,
    isLoading,
    updateData,
    deleteData,
}: IProps) {
    const {
        options: { norms },
        updateValue,
    } = useFiltersContext();
    const benchmarkNorm = useBenchmarkNorm();
    const [query, setQuery] = useState('');
    const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([]);
    const [appliedQuery, setAppliedQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    // Set when the user applies a query change, so the next search results auto-select
    // all items. The saved set's own selection is restored on initial load instead.
    const [selectAllOnNextResults, setSelectAllOnNextResults] = useState(false);

    const {
        data: searchData,
        isLoading: isSearchLoading,
    } = useCustomBenchmarkSearch(appliedQuery);

    const categoryOptions = useMemo(
        (): string[] => Array.from(
            new Set(
                (searchData?.items ?? [])
                    .map(item => item.itemCategory)
                    .filter((category): category is string => !!category)
            )
        ).sort(),
        [searchData?.items]
    );

    // By default, every category is selected whenever a new search resolves.
    useEffect(() => {
        setSelectedCategories(categoryOptions);
    }, [categoryOptions]);

    const filteredItems = useMemo(
        () => {
            const items = searchData?.items ?? [];
            if (selectedCategories.length === 0) return items;
            return items.filter(item => selectedCategories.includes(item.itemCategory ?? ''));
        },
        [searchData?.items, selectedCategories]
    );

    useEffect(() => {
        setQuery(data?.query || '');
        setAppliedQuery(data?.query || '');
    }, [data?.query]);

    useEffect(() => {
        setSelectedItemsIds(data?.itemIds || []);
    }, [data?.itemIds]);

    // After the user changes the query, the freshly-returned items are all selected
    // automatically. We key off the user's action (the flag) rather than comparing
    // queries, so re-entering the saved set's original query still selects everything.
    useEffect(() => {
        if (selectAllOnNextResults && searchData?.items) {
            setSelectedItemsIds(searchData.items.map(item => item.id ?? 0));
            setSelectAllOnNextResults(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData?.items]);

    const applyQuery = useCallback(
        () => {
            if (query !== appliedQuery && !equalInLC(query, '')) {
                setAppliedQuery(query);
                setSelectedItemsIds([]);
                setSelectedCategories([]);
                setSelectAllOnNextResults(true);
            }
        },
        [query, appliedQuery]
    );

    const selectedItems = useMemo(
        (): iSelectOption[] => {
            if (!searchData?.items) return [];

            return searchData.items
                .filter(item => selectedItemsIds.includes(item.id ?? 0))
                .map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                }));
        },
        [searchData?.items, selectedItemsIds]
    );

    const setSelectedItems = useCallback(
        (items: iSelectOption[]) => {
            setSelectedItemsIds(items.map(i => i.id as number));
        },
        []
    );

    const hasAppliedQuery = useMemo(
        () => !!appliedQuery,
        [appliedQuery]
    );

    // Number of selected items among the currently-visible (filtered) rows.
    const visibleSelectedCount = useMemo(
        () => {
            const selectedIds = new Set(selectedItemsIds);
            return filteredItems.filter(i => selectedIds.has(i.id ?? 0)).length;
        },
        [filteredItems, selectedItemsIds]
    );

    const mappedItemsForSorting = useMemo(
        () => filteredItems.map((item, index,) => ({
            ...item,
            [SortingColumns.OriginalOrder]: index + 1,
            [SortingColumns.Date]: item.date ? new Date(item.date) : null,
            [SortingColumns.Price]: item.price ? parseFloat((item.price ?? '').replace(/[^0-9.-]+/g, '')) : null,
            [SortingColumns.Category]: item.itemCategory ?? '',
        })),
        [filteredItems]
    );

    const {
        data: sortedData,
        sortingColumn,
        setSortingColumn,
        sortingDirection,
        setSortingDirection,
    } = useFilterSort(mappedItemsForSorting, SortingColumns.OriginalOrder);

    useEffect(() => {
        setSortingDirection(Sort.Asc);
    }, []);

    const sortingOptions = useMemo(
        (): iSortingOption[] => [
            { id: SortingColumns.Date, name: SortingColumns.Date },
            { id: SortingColumns.Chain, name: SortingColumns.Chain, defaultDirection: Sort.Asc,},
            { id: SortingColumns.Name, name: SortingColumns.Name, defaultDirection: Sort.Asc, },
            { id: SortingColumns.Price, name: SortingColumns.Price },
            { id: SortingColumns.Description, name: SortingColumns.Description, defaultDirection: Sort.Asc, },
            { id: SortingColumns.Category, name: SortingColumns.Category, defaultDirection: Sort.Asc, },
        ],
        []
    );

    const applySort = useCallback(
        (value: iTableSorting) => {
            setSortingColumn(value.id as keyof (typeof filteredItems)[number]);
            setSortingDirection(value.direction);
        },
        [setSortingColumn, setSortingDirection]
    );

    // Toggling a category not only shows/hides its rows but also selects/deselects all of that
    // category's concepts within the returned results (LNRT-219). We diff the incoming category
    // list against the current one and add/remove the matching item ids accordingly.
    const handleCategoriesChange = useCallback(
        (nextCategories: string[]) => {
            const items = searchData?.items ?? [];
            const nextSet = new Set(nextCategories);
            const added = new Set(nextCategories.filter(c => !selectedCategories.includes(c)));
            const removed = new Set(selectedCategories.filter(c => !nextSet.has(c)));

            if (added.size || removed.size) {
                const idsToAdd = items
                    .filter(i => added.has(i.itemCategory ?? ''))
                    .map(i => i.id ?? 0);
                const idsToRemove = new Set(
                    items
                        .filter(i => removed.has(i.itemCategory ?? ''))
                        .map(i => i.id ?? 0)
                );

                setSelectedItemsIds(prev => {
                    const merged = new Set(prev);
                    idsToAdd.forEach(id => merged.add(id));
                    idsToRemove.forEach(id => merged.delete(id));
                    return Array.from(merged);
                });
            }

            setSelectedCategories(nextCategories);
        },
        [searchData?.items, selectedCategories]
    );

    const tableConfig = useCustomBenchmarkSetTableConfig(
        sortedData,
        {
            options: categoryOptions,
            value: selectedCategories,
            onChange: handleCategoriesChange,
            isDisabled: isSearchLoading,
        }
    );

    const applyData = useCallback(
        async () => {
            await updateData({
                query: appliedQuery,
                itemIds: selectedItemsIds,
            });
            onClose();
            updateValue({
                norm: benchmarkNorm?.id
            });
        },
        [appliedQuery, selectedItemsIds, updateData, onClose, updateValue]
    );

    const resetData = useCallback(
        () => {
            updateValue({
                norm: norms[0].id,
            });
            deleteData();
            setSelectedItemsIds([]);
            setQuery('');
            setAppliedQuery('');
            setSelectedCategories([]);
        },
        [deleteData, onClose]
    );

    // Select all / deselect all act on the currently-visible (category-filtered) rows,
    // preserving selections made under other categories.
    const selectAll = useCallback(
        () => setSelectedItemsIds(prev => Array.from(
            new Set([...prev, ...filteredItems.map(i => i.id ?? 0)])
        )),
        [filteredItems]
    );

    const deselectAll = useCallback(
        () => {
            const visibleIds = new Set(filteredItems.map(i => i.id ?? 0));
            setSelectedItemsIds(prev => prev.filter(id => !visibleIds.has(id)));
        },
        [filteredItems],
    );

    const placeholders = useMemo(
        () => shuffle(INPUT_PLACEHOLDERS),
        []
    );

    const hasData = useMemo(
        () => !!data?.itemIds?.length && !!data?.query?.length,
        [data?.itemIds?.length, data?.query?.length]
    )

    return createPortal((
        // Close on mousedown rather than click: a text-selection drag that starts inside the panel
        // and releases on the backdrop dispatches its click to the common ancestor (the backdrop),
        // which used to close the modal (LNRT-219). Keying off mousedown means the press inside the
        // panel is swallowed by the inner's stopPropagation, so the modal stays open.
        <div
            className="ConceptLockerCustomBenchmarkSetPopup"
            onMouseDown={onClose}
        >
            {isLoading && <Loader/>}
            <Wrapper>
                <div
                    className="ConceptLockerCustomBenchmarkSetPopup__inner"
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <button
                        onClick={onClose}
                        className="modal-window__close"
                    >×
                    </button>
                    <div className="ConceptLockerCustomBenchmarkSetPopup__title">
                        {hasData ? 'EDIT': 'CREATE'} CUSTOM BENCHMARK SET
                    </div>
                    <p>Build a custom benchmark set for more tailored concept comparisons.</p>
                    <p>To compare against Same Category (default), Same Segment, or All Items, close this window and
                        choose a standard benchmark group from the dropdown.</p>
                    <br/>
                    <HeaderBar style={{ gap: 16 }} className="ConceptLockerCustomBenchmarkSetPopup__search-panel">
                        <form
                            className="ConceptLockerCustomBenchmarkSetPopup__search-form"
                            onSubmit={(event) => {
                                event.preventDefault();
                                applyQuery();
                            }}
                        >
                            <div style={{ position: 'relative', flex: '0 1 100%' }}>
                                <TextInput
                                    value={query}
                                    setValue={setQuery}
                                    placeholder=""
                                    className="ConceptLockerCustomBenchmarkSetPopup__search-input"
                                    autoFocus
                                />
                                <div
                                    className="ConceptLockerCustomBenchmarkSetPopup__search-input is-placeholder"
                                    style={{
                                        opacity: query === '' ? undefined : 0,
                                    }}
                                >
                                    <Typewriter
                                        loop={0}
                                        typeSpeed={50}
                                        deleteSpeed={10}
                                        delaySpeed={3000}
                                        words={placeholders}
                                    />
                                </div>
                            </div>
                            <Button
                                modifiers={['platform']}
                                style={{
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    cursor: 'pointer',
                                }}
                            >
                                <MdKeyboardArrowRight style={{ width: 30, height: 30 }}/>
                            </Button>
                        </form>
                        <div style={{
                            flex: '1 0',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                            <BsStars style={{ width: 24, height: 24, marginRight: 4 }}/>
                            Give a short description of items you want in your benchmark set - AI will do the rest!
                        </div>
                    </HeaderBar>
                    <div className={cn(
                        "ConceptLockerCustomBenchmarkSetPopup__table",
                        hasAppliedQuery && 'is-open'
                    )}>
                        <SelectedOptionsProvider value={selectedItems} setValue={setSelectedItems}>
                            <TableSortingContext.Provider value={{
                                value: {
                                    id: sortingColumn,
                                    direction: sortingDirection,
                                },
                                setValue: applySort,
                                options: sortingOptions,
                            }}>
                                <TableContext.Provider value={{ config: tableConfig }}>
                                    <VirtualizedTable
                                        rowsCount={filteredItems.length}
                                        isLoading={isSearchLoading}
                                        rowHeight={48}
                                        hideDataFallback
                                        style={{
                                            minHeight: '0',
                                        }}
                                    />
                                </TableContext.Provider>
                            </TableSortingContext.Provider>
                        </SelectedOptionsProvider>
                    </div>

                    {hasAppliedQuery && (
                        <div className="ConceptLockerCustomBenchmarkSetPopup__footer">
                            <div className="ConceptLockerCustomBenchmarkSetPopup__footer-part">
                                <Button modifiers={['naked']} onClick={selectAll} style={{ paddingInline: 4 }}>
                                    select all
                                </Button> |
                                <Button modifiers={['naked']} onClick={deselectAll} style={{ paddingInline: 4 }}>
                                    deselect all
                                </Button>
                            </div>
                            <div className="ConceptLockerCustomBenchmarkSetPopup__footer-part">
                                <PlatformButton onClick={resetData}>
                                    RESET
                                </PlatformButton>
                                <PlatformButton
                                    onClick={applyData}
                                    isDisabled={selectedItemsIds.length === 0}
                                >
                                    APPLY
                                </PlatformButton>
                            </div>
                            <div className="ConceptLockerCustomBenchmarkSetPopup__footer-part">
                                {visibleSelectedCount} of {filteredItems.length} items selected
                            </div>
                        </div>
                    )}
                </div>
            </Wrapper>
        </div>
    ), document.getElementById('modal-root')!);
}